import * as React from "react";
import { ChevronDown } from "lucide-react";

const SelectContext = React.createContext(null);

const Select = ({ children, value, onValueChange, defaultValue }) => {
  const [selectedValue, setSelectedValue] = React.useState(value || defaultValue || "");
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef(null);

  const handleValueChange = (newValue) => {
    setSelectedValue(newValue);
    onValueChange?.(newValue);
    setOpen(false);
  };

  return (
    <SelectContext.Provider value={{ value: selectedValue, onValueChange: handleValueChange, open, setOpen, triggerRef }}>
      <div className="relative">{children}</div>
    </SelectContext.Provider>
  );
};

const useSelect = () => {
  const context = React.useContext(SelectContext);
  if (!context) throw new Error("Select components must be used within Select");
  return context;
};

const SelectTrigger = React.forwardRef(({ className = "", children, ...props }, ref) => {
  const { open, setOpen, value } = useSelect();
  
  return (
    <button
      ref={ref}
      type="button"
      onClick={() => setOpen(!open)}
      className={`flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a4d2e] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      {...props}
    >
      <span className={value ? "" : "text-gray-400"}>{children}</span>
      <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
  );
});
SelectTrigger.displayName = "SelectTrigger";

const SelectValue = ({ placeholder }) => {
  const { value } = useSelect();
  return <>{value || placeholder}</>;
};

const SelectContent = ({ children, className = "" }) => {
  const { open } = useSelect();
  
  if (!open) return null;
  
  return (
    <div className={`absolute z-50 w-full mt-1 rounded-md border border-gray-200 bg-white shadow-lg ${className}`}>
      <div className="p-1">{children}</div>
    </div>
  );
};

const SelectItem = React.forwardRef(({ className = "", children, value, ...props }, ref) => {
  const { onValueChange, value: selectedValue } = useSelect();
  const isSelected = selectedValue === value;
  
  return (
    <div
      ref={ref}
      onClick={() => onValueChange(value)}
      className={`relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-gray-100 focus:bg-gray-100 ${isSelected ? "bg-gray-100" : ""} ${className}`}
      {...props}
    >
      {isSelected && (
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </span>
      )}
      {children}
    </div>
  );
});
SelectItem.displayName = "SelectItem";

export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem };
